<template>
    <div class="filter-row-wrapper">
        <a-popover
            :visible="selected"
            trigger="click"
            @visibleChange="(isVisible) => (isVisible ? onSelected() : onCanceled())"
            placement="left"
            :overlayClassName="'no-padding'"
        >
            <div slot="content">
                <SingleFilterEditor v-model="clone" :on-closed="onCanceled" />
            </div>
            <div class="filter-row">
                <div class="filter-item" :class="{ active: selected }">
                    <div class="filter-remove">
                        <div class="control-button">
                            <md-button
                                class="md-icon-button md-mini"
                                @click="
                                    (e) => {
                                        e.stopPropagation();
                                        onRemoved(clone.id);
                                    }
                                "
                            >
                                <md-icon>delete_outline</md-icon>
                            </md-button>
                        </div>
                    </div>
                    <div class="filter-icon">
                        <AttributeIconPreview :attribute="clone.attribute" />
                    </div>
                    <div class="filter-item-text">
                        <span v-if="clone.attribute === 'available_agents'">
                            {{
                                $i18n.locale !== "tr"
                                    ? `${$t(`chatbotv2.attr.${clone.attribute}`)} (${$t(
                                          "chatbotv2.in"
                                      )} ${getDepartmentTitle(clone.departmentId)})`
                                    : `${$t(`chatbotv2.attr.${clone.attribute}`)} ( ${getDepartmentTitle(
                                          clone.departmentId
                                      )} ${$t("chatbotv2.in")})`
                            }}
                        </span>
                        <span v-if="clone.attribute === 'segments'">
                            {{ `${$t(`chatbotv2.attr.${clone.attribute}`)}` }}
                        </span>
                        <span v-if="clone.attribute === 'tags'">
                            {{ `${$t(`chatbotv2.attr.${clone.attribute}`)}` }}
                        </span>
                        <span v-else>
                            {{
                                isLocalizedAttribute(clone.attribute)
                                    ? $t(`chatbotv2.attr.${clone.attribute}`)
                                    : clone.attribute.startsWith("contact_property_")
                                    ? visitorContactAttributes[clone.attribute.replace("contact_property_", "")]
                                    : clone.attribute
                            }}
                        </span>
                        {{ getOperatorName() }}
                        <span v-if="isPayloadRequired()">{{ getAttributeValue() }} </span>
                    </div>
                </div>
            </div>
        </a-popover>
        <a-select
            :value="operator"
            @change="onOperatorChanged"
            @click="
                (e) => {
                    e.stopPropagation();
                }
            "
            :class="{ hovered: groupSelectHovered }"
            @mouseenter="() => selectGroupHoverStateChanged(true)"
            @mouseleave="() => selectGroupHoverStateChanged(false)"
            v-if="addFilterMode ? showOperatorSelect : true"
        >
            <a-select-option value="or"> {{ $t("chatbotv2.or") }}</a-select-option>
            <a-select-option value="and"> {{ $t("chatbotv2.and") }}</a-select-option>
        </a-select>
        <div class="filter-row small" v-else>
            <a-popover
                v-model="attributePickerVisible"
                trigger="click"
                placement="left"
                :overlayClassName="'no-padding'"
            >
                <div slot="content">
                    <AttributePicker :default-selected="null" :pick="onAttributePicked" :filter-mode="true" />
                </div>
                <div class="add-filter-button">
                    <span class="material-icons"> add </span>
                    {{ $t("chatbotv2.addFilter") }}
                </div>
            </a-popover>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import { generateRandomId } from "../../../../../../../utils/session";
import systemAttributes from "../../../../utils/systemAttributes";
import _ from "lodash";
import filterOperators from "../../../../utils/filterOperators";
import AttributeIconPreview from "../../common/AttributeIconPreview";
import SingleFilterEditor from "./SingleFilterEditor";
import AttributePicker from "../../pickers/AttributePicker";
import CONFIG from "../../../../../../../config/config";
import { Attributes, Entities } from "../../../../palatteEnums";
import { mapActions, mapState } from "vuex";

const log = log2("SingleFilter");
export default {
    name: "SingleFilter",
    components: {
        AttributeIconPreview,
        SingleFilterEditor,
        AttributePicker,
    },
    props: {
        value: {
            type: Object,
            required: true,
            default: () => ({
                id: generateRandomId(),
                rule: "greater_or_equal",
                attribute: "email",
                value: 0,
            }),
        },
        operator: {
            type: String,
            required: true,
            default: "or",
        },
        onOperatorChanged: {
            type: Function,
            required: true,
        },
        enableOperatorChanged: {
            type: Boolean,
            required: true,
            default: true,
        },
        selected: {
            type: Boolean,
            required: true,
            default: false,
        },
        onSelected: {
            type: Function,
            required: true,
        },
        onCanceled: {
            type: Function,
            required: true,
        },
        onRemoved: {
            type: Function,
            required: true,
        },
        groupSelectHovered: {
            type: Boolean,
            required: true,
        },
        onGroupSelectHovered: {
            type: Function,
            required: true,
        },
        addFilterMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        showOperatorSelect: {
            type: Boolean,
            required: false,
            default: true,
        },
        onAttributePicked: {
            type: Function,
            required: false,
            default: () => {},
        },
    },
    data() {
        return {
            attributePickerVisible: false,
            updateModalVisible: false,
            clone: _.cloneDeep(this.value),
            Attributes,
            Entities,
            systemAttributes,
        };
    },
    computed: {
        ...mapState({
            subjects: (state) => state.subject.subjects.data,
            visitorContactAttributes: (state) =>
                (state.contactProperty.data || []).reduce((a, b) => {
                    a[b._id] = b.label;
                    return a;
                }, {}),
            segments: (state) => state.CB.segments,
            tags: (state) => state.tag.tags.data,
            channels: (state) => state.channel.channels,
        }),
    },
    methods: {
        ...mapActions({
            getSubjects: "subject/getSubjects",
        }),
        isLocalizedAttribute(attribute) {
            const isAkinon = attribute.startsWith("akinon");
            const isProbel = attribute.startsWith("probel");
            const isEntity = Entities.includes(attribute);
            const isSystemAttributes = systemAttributes.find((attr) => attr.value === attribute);
            return isAkinon || isProbel || isEntity || isSystemAttributes;
        },
        selectGroupHoverStateChanged(hovered) {
            log("selectGroupHoverStateChanged", hovered);
            this.onGroupSelectHovered(hovered);
        },
        getAttributeValue() {
            log("getAttributeValue", this.clone.attribute);
            if (this.clone.attribute === "department") {
                const foundSubject = this.subjects.find((x) => x._id === this.clone.value);
                const langs = Object.keys(foundSubject ? foundSubject.languageContent : {});
                const targetLang = langs.includes(this.$i18n.locale) ? this.$i18n.locale : langs[0];
                return _.get(foundSubject, `languageContent.${targetLang}.name`);
            } else if (this.clone.attribute === "channelId") {
                return this.getChannelName(this.clone.value);
            } else if (this.clone.attribute === "probel_hasta_cinsiyet") {
                if (this.clone.value === CONFIG.PROBEL.GENDER_TYPES.MALE) return "Erkek";
                else if (this.clone.value === CONFIG.PROBEL.GENDER_TYPES.FEMALE) return "Kadın";
                else return "-";
            } else if (this.clone.attribute === "probel_kimlik_tipi") {
                if (this.clone.value === CONFIG.PROBEL.IDENTITY_TYPES.TC) return "TC Vatandaşı";
                else if (this.clone.value === CONFIG.PROBEL.IDENTITY_TYPES.FOREIGN) return "Yabancı";
                else return "-";
            } else if (this.clone.attribute === "segments") {
                return this.getSegmentTitle(this.clone.value);
            } else if (this.clone.attribute === "tags") {
                return this.getTagsName(this.clone.value);
            } else {
                return this.clone.value;
            }
        },
        getDepartmentTitle(departmentId) {
            const foundSubject = this.subjects.find((x) => x._id === departmentId);
            const langs = Object.keys(foundSubject ? foundSubject.languageContent : {});
            const targetLang = langs.includes(this.$i18n.locale) ? this.$i18n.locale : langs[0];
            return _.get(foundSubject, `languageContent.${targetLang}.name`, `${this.$t("message.allDepartments")}`);
        },
        getSegmentTitle(segmentId) {
            const segment = this.segments.find((x) => x._id === segmentId);
            return segment ? segment.name : "";
        },
        getTagsName(tagId) {
            const tag = this.tags.find((x) => x._id === tagId);
            return tag ? tag.name : "";
        },
        getChannelName(channelId) {
            const channel = this.channels.find((x) => x._id === channelId);
            return channel?.name || "";
        },
        getOperatorName() {
            return this.$t(`chatbotv2.filters.${this.clone.rule}`);
        },
        isPayloadRequired() {
            const foundOperator = filterOperators.find((x) => x.value === this.clone.rule);
            return _.get(foundOperator, "payloadRequired", true); //i18n
        },
    },
    mounted() {
        log("mounted", this.value);
        if (this.clone.attribute === "department") {
            if (this.subjects.length === 0) {
                this.getSubjects();
            }
        }
    },
    watch: {
        clone: {
            handler(newValue) {
                this.$emit("input", newValue);
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.filter-row-wrapper {
    grid-column-gap: 10px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 60px;
    width: 100%;

    .ant-select {
        height: 37px;
        max-width: 70px;
        width: 70px;

        &:hover {
            .ant-select-selection {
                border-color: #40a9ff !important;
                border-right-width: 1px !important;
                background: #40a9ff52 !important;
            }
        }

        &.hovered {
            .ant-select-selection {
                border-color: #40a9ff !important;
                border-right-width: 1px !important;
                background: #40a9ff52 !important;
            }
        }

        .ant-select-selection--single {
            height: 37px;
        }
    }
}

.filter-row {
    .filter-item {
        background: #f5f5f5;
        border: 1px solid #f5f5f5;
        cursor: pointer;
        align-items: center;
        border-radius: 4px;
        display: flex;
        padding: 8px 5px;
        position: relative;
        transition: background-color 0.3s, border 0.3s, opacity 0.3s;

        .filter-icon {
        }

        &.active {
            background: #006cff;
            border-color: #006cff;

            .filter-icon {
                color: #fff;
            }

            .filter-item-text {
                color: #fff;
            }

            &:hover {
                background: #006cff;
                border-color: #006cff;
            }
        }

        &:hover {
            background: transparent;
            border-color: #006cff;

            .filter-remove {
                opacity: 1;
            }
        }

        .filter-remove {
            opacity: 0;
            position: absolute;
            right: -8px;
            top: -14px;
            transition: opacity 0.3s;

            .md-button {
                background-color: #fff;
                align-items: center;
                border-radius: 100%;
                box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                cursor: pointer;
                display: flex;
                justify-content: center;
                margin-bottom: 7px;
                transition: background-color 0.3s;
                height: 30px;
                width: 30px;
                min-height: 30px !important;
                min-width: 30px !important;

                .md-icon {
                    color: #006cff;
                }
            }
        }

        .filter-icon {
            align-items: center;
            color: #006cff;
            display: flex;
            font-size: 15px;
            justify-content: center;
            margin-right: 10px;
            transition: color 0.3s;
            width: 25px;
        }

        .filter-item-text {
            font-size: 16px;
            line-height: 20px;
            overflow: hidden !important;
            text-overflow: ellipsis;
            transition: color 0.3s;
            white-space: nowrap;
            -webkit-line-clamp: 1;

            span {
                font-weight: 600;
            }
        }
    }

    &.small {
        align-items: center;
        color: #006cff;
        cursor: pointer;
        display: flex;
        height: 38px;
        transition: all 0.3s;

        .add-filter-button {
            padding: 0 !important;
            border: unset !important;
            width: 100px !important;
        }

        span {
            font-weight: 600;
            font-size: 15px;
            line-height: 19px;
        }
    }
}
</style>
